import React, { useRef, useEffect, useLayoutEffect, useMemo, useCallback, useState } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { useToast } from '@/contexts/ToastContext';
import { useDirectThreadMessages } from '@/hooks/useDirectThreadMessages';
import { useDirectThreadMeta } from '@/hooks/useDirectThreadMeta';
import { useDirectThreadActions } from '@/hooks/useDirectThread';
import { CloseIcon } from '@/components/ui/Icons';
import '@/components/ui/ContextMenu/ContextMenu.css';
import '@/styles/dnd.css';
import { MessengerChatHeader } from '@/components/Messenger/shared/chrome/MessengerChatHeader';
import { ChatCanvas } from '@/components/Messenger/canvas/ChatCanvas';
import {
  DirectThreadComposerPanel,
  type DirectThreadComposerPanelHandle,
} from '@/components/Messenger/direct/DirectThreadComposerPanel';
import { DirectThreadMessageFeed } from '@/components/Messenger/direct/DirectThreadMessageFeed';
import {
  directThreadSetError,
  getDirectThreadScrollResume,
  getDirectThreadSnapshot,
  directThreadDeleteMessage,
  directThreadEditMessage,
  directThreadPinMessage,
  directThreadToggleMessageLike,
  directThreadUnpinMessage,
} from '@/components/Messenger/cache/directThreadCacheStore';
import { usePresence } from '@/hooks/usePresence';
import { useMessengerTypingPeers } from '@/hooks/useMessengerTypingPeers';
import { formatMessengerGroupTypingLabel } from '@/components/Messenger/shared/messengerGroupTypingLabel';
import { useThreadReadTracking } from './useThreadReadTracking';
import { useMessageJumpNavigation } from './useMessageJumpNavigation';
import {
  isMessengerTailStuckToVisualBottom,
  isMessengerViewportAtBottom,
  MESSENGER_AT_BOTTOM_THRESHOLD_PX,
  scrollMessengerTailIntoView,
  shiftViewportScrollTop,
} from '@/components/Messenger/shared/viewport/messengerViewportScroll';
import type { MessengerMessage, MessengerThread } from '@/api/client';
import { MESSENGER_SIDEBAR_SEARCH_HIGHLIGHT_EMPTY } from '@/constants/messengerSearchHighlight';
import { MessengerPinnedBar } from '@/components/Messenger/shared/pins/MessengerPinnedBar';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function messengerMessageLikeFingerprint(m: MessengerMessage): string {
  return `${m.like_count ?? 0}|${m.liked_by_me ? 1 : 0}|${JSON.stringify(m.like_likers_preview ?? [])}`;
}

interface DirectThreadViewProps {
  threadId: string;
  peerId: string;
  peerName: string;
  /** Как бейдж в сайдбаре: unread_count треда минус вычитание по viewport. */
  fabUnreadCount: number;
  onClose?: () => void;
  /** Пока false — не вызываем markRead (поповер закрыт / уходит анимация). */
  readReceiptsActive?: boolean;
  /** См. ChatView — догон низа при повторном открытии попапа. */
  messengerSurfaceVisible?: boolean;
  /** Входящие в пачке сразу в viewport — скрыть бейджи до прихода данных с сервера. */
  onVisibleIncomingPeerMessages?: (count: number) => void;
  /** Пересылка одного сообщения: открыть modal выбора получателя */
  onForwardMessage?: (msg: MessengerMessage) => void;
  /** Пересылка нескольких сообщений (bulk) */
  onForwardMessages?: (msgs: MessengerMessage[]) => void;
  /** Сообщения для пересылки, прикреплённые над полем ввода (телепорт) */
  initialForwardMsgs?: MessengerMessage[];
  /** Очистить pending пересылки в ChatShell (отправка, отмена стрипа) */
  onConsumePendingForwardDraft?: () => void;
  /** Все треды для поиска имён участников при ответе */
  allThreads?: MessengerThread[];
  isSavedThread?: boolean;
  /** После успешного выбора получателя в модалке пересылки — сбросить выделение в этом треде */
  forwardSelectionClearSignal?: { nonce: number; threadId: string } | null;
  /** Локальная отправка завершилась и должна обновить summary треда в ChatShell без refetch. */
  onThreadMessageCommitted?: (msg: MessengerMessage) => void;
  /** Поиск в сайдбаре: загрузить контекст, перейти и подсветить конкретное сообщение. */
  jumpToMessage?: { threadId: string; messageId: string; nonce: number } | null;
  onJumpToMessageSettled?: (nonce: number) => void;
  variant?: 'direct' | 'group';
  /** Только variant=group: кнопка меню (три точки) слева от «Закрыть» */
  groupHeaderMenu?: React.ReactNode;
  /** variant=group: отображаемые имена участников (person_id → display_name) для индикатора «печатает» */
  groupMemberDisplayById?: Record<string, string>;
  /** Слова строки поиска сайдбара — подсветка в тексте сообщений треда */
  sidebarSearchHighlightWords?: readonly string[];
}

export function DirectThreadView({
  threadId,
  peerId,
  peerName,
  fabUnreadCount,
  onClose,
  readReceiptsActive = false,
  messengerSurfaceVisible = true,
  onVisibleIncomingPeerMessages,
  onForwardMessage,
  onForwardMessages,
  initialForwardMsgs,
  onConsumePendingForwardDraft,
  allThreads,
  isSavedThread = false,
  forwardSelectionClearSignal = null,
  onThreadMessageCommitted,
  jumpToMessage = null,
  onJumpToMessageSettled,
  variant = 'direct',
  groupHeaderMenu,
  groupMemberDisplayById,
  sidebarSearchHighlightWords = MESSENGER_SIDEBAR_SEARCH_HIGHLIGHT_EMPTY,
}: DirectThreadViewProps) {
  const { user } = useAuth();
  const { showError: showToastError } = useToast();
  const currentUserId = user?.id ?? null;

  const currentUserDisplayName = useMemo(() => {
    if (!user) return 'Вы';
    const fn = (user.first_name ?? '').trim();
    const ln = (user.last_name ?? '').trim();
    const full = `${fn} ${ln}`.trim();
    return full || 'Вы';
  }, [user]);

  /** Список тредов с сайдбара меняет ссылку на каждый socket/fetch — в ЛС нужен актуальный поиск пира; в группе не используем. */
  const allThreadsRef = useRef(allThreads);
  allThreadsRef.current = allThreads;

  const peerPresence = usePresence(isSavedThread || variant === 'group' ? '' : peerId);
  const typingPeerIds = useMessengerTypingPeers(
    isSavedThread ? null : threadId
  );
  const isPeerTyping = typingPeerIds.length > 0;

  const viewportRef = useRef<HTMLElement | null>(null);
  const composerPanelRef = useRef<DirectThreadComposerPanelHandle | null>(null);
  const lastDirectThreadToastError = useRef<string | null>(null);
  const lastHandledJumpNonceRef = useRef<number | null>(null);
  const inFlightJumpNonceRef = useRef<number | null>(null);
  const initialScrollSettleRafsRef = useRef<number[]>([]);
  const [initialScrollSettled, setInitialScrollSettled] = useState(false);
  const [jumpScrollIntentSignal, setJumpScrollIntentSignal] = useState(0);
  const [settledJumpNonce, setSettledJumpNonce] = useState<number | null>(null);
  /** Скролл после лайка — в useLayoutEffect сразу после коммита DOM (без rAF-дрожи). */
  const pendingLikeScrollRef = useRef<{
    messageId: string;
    stickyBefore: boolean;
    nearBottomBefore: boolean;
    prevSlotHeight: number;
  } | null>(null);

  /** Отслеживание лайков по id сообщения — для докрутки при чужом лайке с сокета (свой лайк идёт через pendingLikeScrollRef). */
  const likeFingerprintByMessageRef = useRef<{ threadId: string; map: Map<string, string> }>({
    threadId: '',
    map: new Map(),
  });

  /** Нельзя мемоизировать только по threadId: кэш скролла (scrollByThread) обновляется без смены треда. */
  const resumeScrollResume = getDirectThreadScrollResume(threadId);

  const resumeCachedScrollTop =
    resumeScrollResume?.kind === 'scrollTop'
      ? resumeScrollResume.top
      : resumeScrollResume?.kind === 'anchor'
        ? resumeScrollResume.fallbackScrollTop
        : undefined;

  const resumeScrollAnchor =
    resumeScrollResume?.kind === 'anchor'
      ? {
          messageId: resumeScrollResume.anchorMessageId,
          offsetFromViewportTop: resumeScrollResume.offsetFromViewportTop,
        }
      : undefined;

  const { messages, pinnedMessages, hasMoreOlder, hasMoreNewer } = useDirectThreadMessages(threadId);
  const { lastReadMessageId, loading, loadingOlder, loadingNewer, error } =
    useDirectThreadMeta(threadId);
  const {
    loadOlder,
    loadNewer,
    jumpToTailWindow,
    sendMessage,
    sendOwnAndForwardedAtomic,
    markReadToBottom,
    retryMessage,
  } = useDirectThreadActions(threadId, currentUserId);

  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  const { jumpToMessageById, runJumpToMessageIntent } = useMessageJumpNavigation({
    threadId,
    viewportRef,
    loadingRef,
    messagesRef,
    setJumpScrollIntentSignal,
    onJumpSettled: onJumpToMessageSettled,
    setSettledJumpNonce,
    lastHandledJumpNonceRef,
  });

  const cancelInitialScrollSettleRafs = useCallback(() => {
    for (const rafId of initialScrollSettleRafsRef.current) {
      cancelAnimationFrame(rafId);
    }
    initialScrollSettleRafsRef.current = [];
  }, []);

  useEffect(() => {
    cancelInitialScrollSettleRafs();
    setInitialScrollSettled(false);
    setSettledJumpNonce(null);
    /** Иначе при возврате в тот же тред с тем же nonce эффект прыжка сразу выходит — shell intent не сбрасывается, вечный revealBlocked. */
    lastHandledJumpNonceRef.current = null;
    inFlightJumpNonceRef.current = null;
    return cancelInitialScrollSettleRafs;
  }, [threadId, cancelInitialScrollSettleRafs]);

  useEffect(() => {
    if (jumpToMessage) return;
    lastHandledJumpNonceRef.current = null;
    inFlightJumpNonceRef.current = null;
  }, [jumpToMessage]);

  useEffect(() => {
    if (!jumpToMessage) return;
    if (jumpToMessage.threadId !== threadId) return;
    if (initialScrollSettled) return;
    const timer = window.setTimeout(() => {
      setInitialScrollSettled((prev) => (prev ? prev : true));
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [jumpToMessage?.threadId, jumpToMessage?.nonce, threadId, initialScrollSettled]);

  useEffect(() => {
    if (!jumpToMessage) return;
    if (jumpToMessage.threadId !== threadId) return;
    if (lastHandledJumpNonceRef.current === jumpToMessage.nonce) return;
    if (inFlightJumpNonceRef.current === jumpToMessage.nonce) return;
    if (!initialScrollSettled) return;
    let cancelled = false;
    inFlightJumpNonceRef.current = jumpToMessage.nonce;
    const { messageId, nonce } = jumpToMessage;

    const run = async () => {
      try {
        await runJumpToMessageIntent({
          messageId,
          nonce,
          cancelled: () => cancelled,
        });
      } finally {
        if (inFlightJumpNonceRef.current === nonce) {
          inFlightJumpNonceRef.current = null;
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [
    threadId,
    initialScrollSettled,
    jumpToMessage?.threadId,
    jumpToMessage?.messageId,
    jumpToMessage?.nonce,
    runJumpToMessageIntent,
  ]);

  useLayoutEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;

    const inner = vp.querySelector('.messengerChat__messagesInner');
    const tailEl = inner?.lastElementChild ?? null;

    if (likeFingerprintByMessageRef.current.threadId !== threadId) {
      likeFingerprintByMessageRef.current = { threadId, map: new Map() };
    }
    const prevLikeMap = likeFingerprintByMessageRef.current.map;
    const nextLikeMap = new Map<string, string>();
    for (const m of messages) {
      nextLikeMap.set(m.id, messengerMessageLikeFingerprint(m));
    }

    let likeFieldsChanged = false;
    for (const [id, sig] of nextLikeMap) {
      if (prevLikeMap.has(id) && prevLikeMap.get(id) !== sig) {
        likeFieldsChanged = true;
        break;
      }
    }

    const pending = pendingLikeScrollRef.current;
    if (pending) {
      pendingLikeScrollRef.current = null;

      const msgSel =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
          ? `[data-dm-msg="${CSS.escape(pending.messageId)}"]`
          : `[data-dm-msg="${pending.messageId.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`;

      if (pending.stickyBefore) {
        scrollMessengerTailIntoView(vp, tailEl, 'instant', { deferSecondSnap: false });
        likeFingerprintByMessageRef.current.map = nextLikeMap;
        return;
      }

      if (pending.nearBottomBefore) {
        const slot = vp.querySelector(msgSel) as HTMLElement | null;
        if (slot) {
          const delta = slot.offsetHeight - pending.prevSlotHeight;
          if (delta !== 0) {
            shiftViewportScrollTop(vp, delta);
          }
        }
      }
      likeFingerprintByMessageRef.current.map = nextLikeMap;
      return;
    }

    likeFingerprintByMessageRef.current.map = nextLikeMap;

    if (!likeFieldsChanged) return;

    const atBottom =
      isMessengerTailStuckToVisualBottom(vp, tailEl, undefined, inner) ||
      isMessengerViewportAtBottom(vp, MESSENGER_AT_BOTTOM_THRESHOLD_PX);
    if (!atBottom) return;

    scrollMessengerTailIntoView(vp, tailEl, 'instant', { deferSecondSnap: false });
  }, [messages, threadId]);

  const getReplyToSenderName = useCallback(
    (msg: MessengerMessage): string => {
      if (msg.sender_id === currentUserId) return 'Вы';
      if (variant === 'group') {
        return (msg.sender_display_name ?? '').trim() || 'Участник';
      }
      const thread = allThreadsRef.current?.find(
        (t) => t.thread_kind === 'direct' && t.peer_id === msg.sender_id
      );
      return thread?.peer_name ?? peerName;
    },
    [currentUserId, peerName, variant]
  );

  const getForwardStripSenderName = useCallback(
    (msg: MessengerMessage): string => {
      const virtualLabel = msg.forwarded_from_label?.trim();
      if (virtualLabel) return virtualLabel;
      if (msg.sender_id === currentUserId) return currentUserDisplayName;
      if (variant === 'group') {
        return (msg.sender_display_name ?? '').trim() || 'Участник';
      }
      const thread = allThreadsRef.current?.find(
        (t) => t.thread_kind === 'direct' && t.peer_id === msg.sender_id
      );
      return thread?.peer_name ?? peerName;
    },
    [currentUserId, currentUserDisplayName, peerName, variant]
  );

  /** Подпись «печатает» — без лишних колбэков на каждое сообщение: при пустом typing не тянем messages в цепочку зависимостей. */
  const groupTypingStatusLabel = useMemo(() => {
    if (variant !== 'group' || typingPeerIds.length === 0) return '';
    const nameBySender: Record<string, string> = {};
    for (const m of messages) {
      const id = m.sender_id;
      if (!id || nameBySender[id]) continue;
      const n = (m.sender_display_name ?? '').trim();
      if (n) nameBySender[id] = n;
    }
    const resolve = (personId: string): string => {
      const fromMembers = groupMemberDisplayById?.[personId]?.trim();
      if (fromMembers) return fromMembers;
      return nameBySender[personId] ?? 'Участник';
    };
    return formatMessengerGroupTypingLabel(typingPeerIds, resolve);
  }, [variant, typingPeerIds, groupMemberDisplayById, messages]);

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const handleToggleSelect = useCallback((msg: MessengerMessage) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(msg.id)) next.delete(msg.id);
      else next.add(msg.id);
      return next;
    });
  }, []);

  useEffect(() => {
    setSelectionMode(selectedIds.size > 0);
  }, [selectedIds]);

  useEffect(() => {
    if (!selectionMode) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exitSelectionMode();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectionMode, exitSelectionMode]);

  const lastForwardClearNonce = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (!forwardSelectionClearSignal) return;
    if (forwardSelectionClearSignal.threadId !== threadId) return;
    if (lastForwardClearNonce.current === forwardSelectionClearSignal.nonce) return;
    lastForwardClearNonce.current = forwardSelectionClearSignal.nonce;
    exitSelectionMode();
  }, [forwardSelectionClearSignal, threadId, exitSelectionMode]);

  const { viewportUnreadAnchorId, onMessagesViewportActivity, reapplyServerUnreadDividerAfterScroll } =
    useThreadReadTracking({
      threadId,
      currentUserId,
      viewportRef,
      messages,
      lastReadMessageId,
      loading,
      loadingOlder,
      loadingNewer,
      hasMoreOlder,
      hasMoreNewer,
      readReceiptsActive,
      onVisibleIncomingPeerMessages,
      markReadToBottom,
      loadOlder,
      loadNewer,
    });

  const handleResumeScrollSettled = useCallback(() => {
    reapplyServerUnreadDividerAfterScroll();
    cancelInitialScrollSettleRafs();

    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        const raf3 = requestAnimationFrame(() => {
          initialScrollSettleRafsRef.current = [];
          setInitialScrollSettled(true);
        });
        initialScrollSettleRafsRef.current = [raf3];
      });
      initialScrollSettleRafsRef.current = [raf2];
    });
    initialScrollSettleRafsRef.current = [raf1];
  }, [
    reapplyServerUnreadDividerAfterScroll,
    cancelInitialScrollSettleRafs,
  ]);

  useEffect(() => {
    lastDirectThreadToastError.current = null;
  }, [threadId]);

  useEffect(() => {
    if (!error) {
      lastDirectThreadToastError.current = null;
      return;
    }
    if (lastDirectThreadToastError.current === error) return;
    lastDirectThreadToastError.current = error;
    showToastError(error);
    directThreadSetError(threadId, null);
  }, [error, threadId, showToastError]);

  const handleDelete = useCallback(
    (msg: MessengerMessage) => {
      if (!window.confirm('Удалить сообщение?')) return;
      void directThreadDeleteMessage(threadId, msg.id, { hardRemove: isSavedThread }).catch(() => {
        /* откат и ошибка в кэше */
      });
    },
    [threadId, isSavedThread]
  );

  const handleToggleMessageLike = useCallback(
    (msg: MessengerMessage) => {
      if (!currentUserId) return;
      if (msg.deleted_at) return;

      const likedBefore = Boolean(msg.liked_by_me);

      const vp = viewportRef.current;
      const inner = vp?.querySelector('.messengerChat__messagesInner') ?? null;
      const tailEl = inner?.lastElementChild ?? null;
      const msgSel =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
          ? `[data-dm-msg="${CSS.escape(msg.id)}"]`
          : `[data-dm-msg="${msg.id.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`;
      const msgSlot = vp?.querySelector(msgSel) as HTMLElement | null;

      let stickyBefore = false;
      let nearBottomBefore = false;
      let prevSlotHeight = 0;
      if (vp && msgSlot) {
        stickyBefore = isMessengerTailStuckToVisualBottom(vp, tailEl, undefined, inner);
        const vr = vp.getBoundingClientRect();
        const mr = msgSlot.getBoundingClientRect();
        nearBottomBefore = mr.bottom >= vr.bottom - MESSENGER_AT_BOTTOM_THRESHOLD_PX;
        prevSlotHeight = msgSlot.offsetHeight;
      }

      void directThreadToggleMessageLike(threadId, msg.id);

      if (!stickyBefore && !nearBottomBefore) return;

      const mAfter = getDirectThreadSnapshot(threadId)?.messages.find((m) => m.id === msg.id);
      if (!mAfter || Boolean(mAfter.liked_by_me) === likedBefore) return;

      pendingLikeScrollRef.current = {
        messageId: msg.id,
        stickyBefore,
        nearBottomBefore,
        prevSlotHeight,
      };
    },
    [currentUserId, threadId]
  );

  const handlePinMessage = useCallback(
    (msg: MessengerMessage) => {
      void directThreadPinMessage(threadId, msg.id).catch(() => {
        /* ошибка будет показана через cache.error */
      });
    },
    [threadId]
  );

  const handleUnpinMessage = useCallback(
    (msg: MessengerMessage) => {
      void directThreadUnpinMessage(threadId, msg.id).catch(() => {
        /* ошибка будет показана через cache.error */
      });
    },
    [threadId]
  );

  const handleStartEdit = useCallback((msg: MessengerMessage) => {
    composerPanelRef.current?.beginEditMessage(msg);
    requestAnimationFrame(() => composerPanelRef.current?.focusInput());
  }, []);

  const handleReply = useCallback((msg: MessengerMessage) => {
    composerPanelRef.current?.clearEditingMessage();
    composerPanelRef.current?.setReplyTo(msg);
    requestAnimationFrame(() => composerPanelRef.current?.focusInput());
  }, []);

  const handleBulkDelete = useCallback(() => {
    const toDelete = messages.filter(
      (m) =>
        selectedIds.has(m.id) &&
        m.sender_id === currentUserId &&
        !m.deleted_at &&
        (isSavedThread || Date.now() - new Date(m.created_at).getTime() < ONE_DAY_MS)
    );
    if (toDelete.length === 0) return;
    const n = toDelete.length;
    const q =
      n === 1
        ? 'Удалить выбранное сообщение?'
        : `Удалить выбранные сообщения (${n})?`;
    if (!window.confirm(q)) return;
    for (const msg of toDelete) {
      void directThreadDeleteMessage(threadId, msg.id, { hardRemove: isSavedThread }).catch(() => {
        /* откат в кэше по каждому сообщению */
      });
    }
    exitSelectionMode();
  }, [messages, selectedIds, currentUserId, threadId, isSavedThread, exitSelectionMode]);

  const handleBulkForward = useCallback(() => {
    const selected = messages.filter((m) => selectedIds.has(m.id) && !m.deleted_at);
    if (selected.length === 0) return;
    if (selected.length === 1 && onForwardMessage) {
      onForwardMessage(selected[0]!);
    } else if (onForwardMessages) {
      onForwardMessages(selected);
    } else if (onForwardMessage) {
      onForwardMessage(selected[0]!);
    }
  }, [messages, selectedIds, onForwardMessage, onForwardMessages]);

  const editMessage = useCallback(
    (messageId: string, body: string) => directThreadEditMessage(threadId, messageId, body),
    [threadId]
  );

  const canDeleteCount = selectionMode
    ? messages.filter(
        (m) =>
          selectedIds.has(m.id) &&
          m.sender_id === currentUserId &&
          !m.deleted_at &&
          (isSavedThread || Date.now() - new Date(m.created_at).getTime() < ONE_DAY_MS)
      ).length
    : 0;

  const composerNode = (
    <DirectThreadComposerPanel
      ref={composerPanelRef}
      threadId={threadId}
      currentUserId={currentUserId}
      sendMessage={sendMessage}
      sendOwnAndForwardedAtomic={sendOwnAndForwardedAtomic}
      editMessage={editMessage}
      resolveForwardAuthorName={getForwardStripSenderName}
      getReplyToSenderName={getReplyToSenderName}
      initialForwardMsgs={initialForwardMsgs}
      onConsumePendingForwardDraft={onConsumePendingForwardDraft}
      onMessageCommitted={onThreadMessageCommitted}
      selectionMode={selectionMode}
      selectedIds={selectedIds}
      canDeleteCount={canDeleteCount}
      onBulkDelete={handleBulkDelete}
      onBulkForward={handleBulkForward}
      onExitSelectionMode={exitSelectionMode}
    />
  );

  const statusLabel = isSavedThread
    ? 'Только для вас'
    : variant === 'group'
    ? groupTypingStatusLabel
    : isPeerTyping
    ? 'печатает…'
    : peerPresence.label;

  const pinnedHeaderControl =
    pinnedMessages.length > 0 ? (
      <MessengerPinnedBar pins={pinnedMessages} onJumpToMessage={jumpToMessageById} />
    ) : undefined;

  const header = (
    <MessengerChatHeader
      headerClassName="chatAssistant__header"
      titleClassName="chatAssistant__title"
      title={isSavedThread ? 'Сохранённые сообщения' : peerName}
      online={!isSavedThread && variant !== 'group' && peerPresence.online}
      statusLabel={statusLabel}
      afterTitle={pinnedHeaderControl}
      right={
        <>
          {variant === 'group' && groupHeaderMenu}
          {onClose ? (
            <button
              type="button"
              className="chatAssistant__close"
              onClick={onClose}
              aria-label="Закрыть"
            >
              <CloseIcon size={20} />
            </button>
          ) : null}
        </>
      }
    />
  );

  const tailMsg = messages[messages.length - 1];
  const dmComposerDisabled = currentUserId == null;
  const messageFeedReady = messages.length > 0 || !loading;
  const messagesRevealBlocked =
    jumpToMessage?.threadId === threadId &&
    settledJumpNonce !== jumpToMessage.nonce;

  return (
    <ChatCanvas
      header={header}
      composer={composerNode}
      tailMessageId={tailMsg?.id}
      headMessageId={messages[0]?.id}
      messageListLength={messages.length}
      messageFeedReady={messageFeedReady}
      contextId={threadId}
      tailSenderId={tailMsg?.message_kind === 'system' ? null : tailMsg?.sender_id ?? null}
      autoScrollOnOwnTailAppendUserId={currentUserId}
      viewportRef={viewportRef}
      resumeScrollTop={resumeCachedScrollTop}
      resumeScrollAnchor={resumeScrollAnchor}
      scrollDownBadgeOverride={fabUnreadCount}
      onResumeScrollSettled={handleResumeScrollSettled}
      onMessagesViewportChange={onMessagesViewportActivity}
      hasRemoteNewerMessages={hasMoreNewer}
      onBeforeJumpToLatest={jumpToTailWindow}
      messengerSurfaceVisible={messengerSurfaceVisible}
      externalScrollIntentSignal={jumpScrollIntentSignal}
      messagesRevealBlocked={messagesRevealBlocked}
      acceptDrop={!dmComposerDisabled}
      onDrop={(e) => composerPanelRef.current?.handleDrop(e)}
    >
      <DirectThreadMessageFeed
        threadId={threadId}
        currentUserId={currentUserId}
        loading={loading}
        viewportUnreadAnchorId={viewportUnreadAnchorId}
        selectionMode={selectionMode}
        selectedIds={selectedIds}
        onToggleSelect={handleToggleSelect}
        onToggleMessageLike={handleToggleMessageLike}
        onRetry={retryMessage}
        onReply={handleReply}
        onForward={onForwardMessage}
        onDelete={handleDelete}
        onStartEdit={handleStartEdit}
        onPin={handlePinMessage}
        onUnpin={handleUnpinMessage}
        onJumpToMessage={jumpToMessageById}
        variant={variant}
        isSavedThread={isSavedThread}
        sidebarSearchHighlightWords={sidebarSearchHighlightWords}
      />
    </ChatCanvas>
  );
}
